import { getSpacing, type MantineSpacing, type TreeNodeData, type UseTreeReturnType } from '@mantine/core';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';

type FlatNode = { node: TreeNodeData; level: number; parent: string | null; hasChildren: boolean; expanded: boolean };

export type VirtualTreeNodePayload = {
  node: TreeNodeData;
  /** 1 for root nodes */
  level: number;
  expanded: boolean;
  hasChildren: boolean;
  selected: boolean;
  /** Spread onto the row element, for its indent, selection state and click handling */
  elementProps: {
    className: string;
    style: CSSProperties;
    onClick: (event: MouseEvent) => void;
    'data-selected': true | undefined;
    'data-value': string;
  };
};

type VirtualTreeProps = {
  data: TreeNodeData[];
  tree: UseTreeReturnType;
  /** The element that scrolls the tree, such as a ScrollArea viewport */
  scrollRef: RefObject<HTMLElement>;
  renderNode: (payload: VirtualTreeNodePayload) => ReactNode;
  levelOffset?: MantineSpacing;
  /** Clicking a row selects it, and the arrow keys move the selection */
  selectOnClick?: boolean;
  /** Starting guess for row heights, before each row is measured */
  estimateRowHeight?: number;
};

/** The nodes under expanded parents, in display order */
function flattenVisible(data: TreeNodeData[], expanded: Record<string, boolean>): FlatNode[] {
  const rows: FlatNode[] = [];
  const visit = (nodes: TreeNodeData[], level: number, parent: string | null) => {
    for (const node of nodes) {
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      const isExpanded = hasChildren && expanded[node.value] === true;
      rows.push({ node, level, parent, hasChildren, expanded: isExpanded });
      if (isExpanded) visit(node.children!, level + 1, node.value);
    }
  };
  visit(data, 1, null);
  return rows;
}

/**
 * A tree driven by Mantine's useTree controller that only mounts the rows in view, so large expanded trees stay
 * responsive. Mantine's Tree renders every expanded row and re-renders all of them on any change, including hover.
 */
function VirtualTree({
  data,
  tree,
  scrollRef,
  renderNode,
  levelOffset = 'md',
  selectOnClick = false,
  estimateRowHeight = 28,
}: VirtualTreeProps) {
  const { expandedState, selectedState, initialize } = tree;
  const rows = useMemo(() => flattenVisible(data, expandedState), [data, expandedState]);
  const indexByValue = useMemo(() => new Map(rows.map((row, index) => [row.node.value, index])), [rows]);

  // The controller tracks the full data so expandAllNodes and collapseAllNodes reach every node, as Tree does
  useEffect(() => {
    initialize(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialize changes identity with the selection
  }, [data]);

  const listRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const list = listRef.current;
    if (!scroller || !list) return;
    const offset = list.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    setScrollMargin(Math.max(0, Math.round(offset)));
  }, [scrollRef]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateRowHeight,
    getItemKey: (index) => rows[index].node.value,
    overscan: 12,
    scrollMargin,
  });

  // The row the keyboard acts on, which follows the selection when there is one
  const [activeValue, setActiveValue] = useState<string | null>(null);
  const [selectedValue] = selectedState;
  useEffect(() => {
    if (selectedValue) setActiveValue(selectedValue);
  }, [selectedValue]);

  // Keep the selected row in view, e.g. after navigating to it from elsewhere
  useEffect(() => {
    const index = selectedValue ? indexByValue.get(selectedValue) : undefined;
    if (index !== undefined) virtualizer.scrollToIndex(index, { align: 'auto' });
    // Only when the selection moves, not whenever rows open or close around it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]);

  const activate = (value: string) => {
    setActiveValue(value);
    if (selectOnClick) tree.select(value);
    const index = indexByValue.get(value);
    if (index !== undefined) virtualizer.scrollToIndex(index, { align: 'auto' });
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (rows.length === 0) return;
    const index = activeValue ? (indexByValue.get(activeValue) ?? -1) : -1;
    const row = index >= 0 ? rows[index] : undefined;

    switch (event.key) {
      case 'ArrowDown':
        activate(rows[Math.min(index + 1, rows.length - 1)].node.value);
        break;
      case 'ArrowUp':
        activate(rows[Math.max(index - 1, 0)].node.value);
        break;
      case 'Home':
        activate(rows[0].node.value);
        break;
      case 'End':
        activate(rows[rows.length - 1].node.value);
        break;
      case 'ArrowRight':
        if (!row?.hasChildren) return;
        if (row.expanded) activate(rows[index + 1].node.value);
        else tree.expand(row.node.value);
        break;
      case 'ArrowLeft':
        if (!row) return;
        if (row.expanded) tree.collapse(row.node.value);
        else if (row.parent) activate(row.parent);
        break;
      case ' ':
      case 'Enter':
        if (!row?.hasChildren) return;
        tree.toggleExpanded(row.node.value);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  const levelOffsetValue = getSpacing(levelOffset);

  return (
    <div
      ref={listRef}
      role="tree"
      tabIndex={0}
      className="virtual-tree"
      onKeyDown={handleKeyDown}
      style={{ position: 'relative', width: '100%', height: virtualizer.getTotalSize() }}
    >
      {virtualizer.getVirtualItems().map((item) => {
        const row = rows[item.index];
        const { value } = row.node;
        const selected = selectedState.includes(value);
        return (
          <div
            key={item.key}
            ref={virtualizer.measureElement}
            data-index={item.index}
            role="treeitem"
            aria-level={row.level}
            aria-expanded={row.hasChildren ? row.expanded : undefined}
            aria-selected={selected}
            data-active={activeValue === value || undefined}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${item.start - scrollMargin}px)`,
            }}
          >
            {renderNode({
              node: row.node,
              level: row.level,
              expanded: row.expanded,
              hasChildren: row.hasChildren,
              selected,
              elementProps: {
                className: 'virtual-tree-label',
                style: { '--label-offset': `calc(${levelOffsetValue} * ${row.level - 1})` } as CSSProperties,
                onClick: () => {
                  setActiveValue(value);
                  if (selectOnClick) tree.select(value);
                  listRef.current?.focus({ preventScroll: true });
                },
                'data-selected': selected || undefined,
                'data-value': value,
              },
            })}
          </div>
        );
      })}
    </div>
  );
}

export default VirtualTree;
